import { VendorInvoice } from 'types/invoices';
import { InvoiceCalculations, InvoiceMetadata } from './types';

/**
 * Calculate invoice totals including subtotal, HST, and grand total
 */
export const calculateInvoiceTotals = (
  vendor: VendorInvoice,
  hstRate: number
): InvoiceCalculations => {
  // net_subtotal_cost/hst_amount are computed server-side (InvoiceComputed, via
  // services/invoiceService.js updateInvoiceTotals) and already have admin fee/forecast fee/CIB
  // share netted in. Do NOT re-derive them from per-meter fields here — those are informational
  // breakdowns of what's already baked into these numbers.
  const meterSubtotal = vendor.net_subtotal_cost ?? 0;
  const meterHst = vendor.hst_amount ?? 0;
  const miscAmount = vendor.misc_charge?.amount ?? 0;
  const miscHst = vendor.misc_charge?.hst_applicable ? miscAmount * (hstRate / 100) : 0;
  const subtotal = meterSubtotal + miscAmount;
  const hstValue = meterHst + miscHst;
  const total = subtotal + hstValue;
  const totalNetGen = vendor.meters.reduce((sum, m) => sum + (m.post_prod_stat_netgen ?? 0), 0);

  return {
    meterSubtotal,
    subtotal,
    hstValue,
    total,
    totalNetGen,
    miscAmount,
    miscHst,
  };
};

/**
 * Generate invoice metadata including invoice number and bill date
 *
 * @param dueDateOffsetDays - Days to subtract from the vendor due date before display.
 * Used by the NSP version of the invoice, which shows the due date 10 days earlier
 * than the vendor-facing copy. Only applied when the vendor's due date is after the
 * 10th of the month.
 */
export const generateInvoiceMetadata = (
  vendor: VendorInvoice,
  monthYear: string,
  dueDateOffsetDays: number = 0
): InvoiceMetadata => {
  let invoiceNo = 'N/A';
  if (vendor.vendor_invoice_prefix && monthYear) {
    const [year, month] = monthYear.split('-');
    const yy = year.slice(-2);
    invoiceNo = `${vendor.vendor_invoice_prefix}${yy}${month}`;
  }

  let billDate = 'N/A';
  if (vendor.vendor_due_date && monthYear) {
    const [year, month] = monthYear.split('-');
    let nextMonth = Number.parseInt(month, 10) + 1;
    let billYear = Number.parseInt(year, 10);
    if (nextMonth > 12) {
      nextMonth = 1;
      billYear += 1;
    }
    const date = new Date(billYear, nextMonth - 1, Number.parseInt(String(vendor.vendor_due_date), 10));
    if (dueDateOffsetDays && Number(vendor.vendor_due_date) > 10) {
      date.setDate(date.getDate() - dueDateOffsetDays);
    }
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    billDate = `${mm}/${dd}/${date.getFullYear()}`;
  }

  return { invoiceNo, billDate };
};

/**
 * Generate sanitized filename for the invoice PDF
 */
export const generateInvoiceFilename = (vendorName: string, monthYear: string, prefix?: string): string => {
  const sanitizedVendorName = vendorName.replaceAll(/[^a-z0-9 ]/gi, ' ').trim();
  const base = sanitizedVendorName ? `${sanitizedVendorName} ${monthYear}.pdf` : `${monthYear}.pdf`;
  return prefix ? `${prefix}${base}` : base;
};
